import { useState, useEffect } from 'react';
import { Spinner, Text, BlockStack } from '@shopify/polaris';
import { navigateToOAuth } from '../utils/appBridge';

interface SessionCheckProps {
  shop: string;
  onSessionValid: () => void;
}

export default function SessionCheck({ shop, onSessionValid }: SessionCheckProps) {
  const [message, setMessage] = useState('Initializing...');

  useEffect(() => {
    const checkAndRefreshSession = async () => {
      if (!shop) {
        console.log('No shop provided, skipping session check');
        onSessionValid();
        return;
      }

      try {
        setMessage('Checking connection...');
        console.log('Checking session for shop:', shop);
        
        // Try to access a protected endpoint to check session
        // Use fetchWithAuth which handles session tokens automatically
        const { fetchWithAuth } = await import('../utils/fetchWithAuth');
        const response = await fetchWithAuth(`/api/admin/settings?shop=${encodeURIComponent(shop)}`);
        
        if (response.ok) {
          // Session is valid
          console.log('✅ Session valid, proceeding...');
          onSessionValid();
          return;
        }

        // Session invalid - need to authenticate
        // Backend has determined session is invalid (server-authoritative)
        console.log('❌ Session invalid (401), redirecting to OAuth...');
        setMessage('Reconnecting to your store...');
        
        // Wait just a moment for better UX
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Use App Bridge Redirect for embedded apps (correct way)
        // This ensures proper top-level navigation without iframe security issues
        console.log('[SessionCheck] Redirecting to OAuth using App Bridge Redirect');
        navigateToOAuth(shop, '/admin');
        
      } catch (error) {
        console.error('Session check error:', error);
        setMessage('Connection error, retrying...');
        
        // Still try to redirect on error using App Bridge Redirect
        await new Promise(resolve => setTimeout(resolve, 500));
        console.log('[SessionCheck] Error occurred, redirecting to OAuth using App Bridge Redirect');
        navigateToOAuth(shop, '/admin');
      }
    };

    checkAndRefreshSession();
  }, [shop, onSessionValid]);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f6f6f7'
    }}>
      <div style={{
        textAlign: 'center',
        padding: '40px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        maxWidth: '400px'
      }}>
        <BlockStack gap="400">
          <Spinner size="large" />
          <Text variant="headingMd" as="h2">
            {message}
          </Text>
          <Text variant="bodyMd" as="p" tone="subdued">
            {message === 'Initializing...' 
              ? 'Please wait...' 
              : 'This happens automatically after server restarts. You\'ll be redirected in a moment...'}
          </Text>
          {message === 'Reconnecting to your store...' && (
            <Text variant="bodySm" as="p" tone="subdued">
              💡 If this takes too long, please reload the page.
            </Text>
          )}
        </BlockStack>
      </div>
    </div>
  );
}
